import PageLayout from "../components/PageLayout";
import { CONTACT } from "../utils/contact";
import "./LegalPage.css";

const TOC = [
  { id: "introduction", label: "Introduction" },
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "services", label: "Services Provided" },
  { id: "accounts", label: "User Accounts" },
  { id: "responsibilities", label: "User Responsibilities" },
  { id: "intellectual-property", label: "Intellectual Property" },
  { id: "payment", label: "Payment and Billing" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "liability", label: "Limitation of Liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "governing-law", label: "Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "termination", label: "Termination" },
  { id: "contact", label: "Contact Information" },
];

export default function TermsAndConditions() {
  return (
    <PageLayout title="Terms & Conditions" wide>
      <p className="legal-updated">
        <strong>Last updated:</strong> June 12, 2026
      </p>

      <nav className="legal-toc" aria-label="Table of contents">
        <h2>Table of Contents</h2>
        <ol>
          {TOC.map((item, i) => (
            <li key={item.id}>
              <a href={`#${item.id}`}>
                {i + 1}. {item.label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          Welcome to Maskanh! These Terms and Conditions (&quot;Terms&quot;)
          govern your use of the Maskanh platform (&quot;Platform&quot;), a
          comprehensive construction, renovation, and home improvement services
          marketplace. By accessing or using our Platform, you agree to be bound
          by these Terms.
        </p>
        <p>
          Maskanh is a digital platform that connects homeowners, property
          managers, and businesses with qualified construction professionals,
          contractors, and service providers. We facilitate the discovery,
          booking, and management of construction and home improvement services.
        </p>
        <p>
          <strong>Key Definitions:</strong>
        </p>
        <ul>
          <li>
            <strong>&quot;User&quot;</strong> refers to any individual or entity
            using the Maskanh Platform.
          </li>
          <li>
            <strong>&quot;Service Provider&quot;</strong> refers to construction
            professionals, contractors, and tradespeople who offer services
            through our Platform.
          </li>
          <li>
            <strong>&quot;Service&quot;</strong> refers to any construction,
            renovation, or home improvement service offered through the Platform.
          </li>
          <li>
            <strong>&quot;Platform&quot;</strong> refers to the Maskanh
            website, mobile application, and related services.
          </li>
          <li>
            <strong>&quot;Content&quot;</strong> refers to any information,
            data, text, images, or other materials posted on the Platform.
          </li>
        </ul>
      </section>

      <section id="acceptance">
        <h2>2. Acceptance of Terms</h2>
        <p>
          By accessing or using the Maskanh Platform, you acknowledge that you
          have read, understood, and agree to be bound by these Terms and our{" "}
          <a href={CONTACT.privacyPolicy}>Privacy Policy</a>. If you do not
          agree to these Terms, you may not access or use our Platform.
        </p>
        <p>
          You represent and warrant that you are at least 18 years old and have
          the legal capacity to enter into these Terms. If you are using the
          Platform on behalf of a company or other legal entity, you represent
          that you have the authority to bind such entity to these Terms.
        </p>
        <p>
          These Terms constitute a legally binding agreement between you and
          Maskanh Inc. Your continued use of the Platform constitutes acceptance
          of any modifications to these Terms.
        </p>
      </section>

      <section id="services">
        <h2>3. Services Provided</h2>
        <p>
          Maskanh provides a digital marketplace platform that connects users
          with qualified construction professionals and service providers. Our
          services include but are not limited to:
        </p>
        <ul>
          <li>
            <strong>Service Discovery:</strong> Browse and search for
            construction and home improvement services
          </li>
          <li>
            <strong>Professional Matching:</strong> Connect with qualified
            contractors, electricians, plumbers, carpenters, painters, and other
            tradespeople
          </li>
          <li>
            <strong>Project Management:</strong> Tools to manage construction
            projects from planning to completion
          </li>
          <li>
            <strong>Communication Platform:</strong> Secure messaging and
            communication between users and service providers
          </li>
          <li>
            <strong>Payment Processing:</strong> Secure payment processing for
            services rendered
          </li>
          <li>
            <strong>Review and Rating System:</strong> Platform for users to
            review and rate service providers
          </li>
          <li>
            <strong>Project Documentation:</strong> Tools for documenting
            project progress and completion
          </li>
        </ul>
        <p>
          <strong>Important Note:</strong> Maskanh acts as a facilitator and
          marketplace. We do not directly provide construction services. All
          construction work is performed by independent service providers who
          are not employees of Maskanh. We are not responsible for the quality,
          safety, or completion of any construction work performed by service
          providers.
        </p>
      </section>

      <section id="accounts">
        <h2>4. User Accounts</h2>

        <h3>4.1 Account Creation</h3>
        <p>
          To access certain features of the Platform, you must create an
          account. When creating an account, you agree to:
        </p>
        <ul>
          <li>Provide accurate, current, and complete information</li>
          <li>
            Maintain and update your account information to keep it accurate and
            current
          </li>
          <li>Be at least 18 years old</li>
          <li>Have the legal capacity to enter into contracts</li>
          <li>Not create multiple accounts for the same person or entity</li>
        </ul>

        <h3>4.2 Account Security</h3>
        <p>You are responsible for:</p>
        <ul>
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>All activities that occur under your account</li>
          <li>
            Notifying Maskanh immediately of any unauthorized use of your
            account
          </li>
          <li>Using a strong, unique password</li>
          <li>Not sharing your account credentials with others</li>
        </ul>

        <h3>4.3 Account Types</h3>
        <p>Maskanh offers different types of accounts:</p>
        <ul>
          <li>
            <strong>Homeowner Accounts:</strong> For individuals seeking
            construction services
          </li>
          <li>
            <strong>Service Provider Accounts:</strong> For construction
            professionals offering services
          </li>
          <li>
            <strong>Business Accounts:</strong> For companies and organizations
          </li>
        </ul>
      </section>

      <section id="responsibilities">
        <h2>5. User Responsibilities</h2>

        <h3>5.1 General Responsibilities</h3>
        <p>As a user of the Maskanh Platform, you agree to:</p>
        <ul>
          <li>
            Use the Platform only for lawful purposes and in accordance with
            these Terms
          </li>
          <li>Provide accurate and truthful information in all communications</li>
          <li>Respect the rights and privacy of other users</li>
          <li>
            Comply with all applicable local, state, and federal laws and
            regulations
          </li>
          <li>Not interfere with or disrupt the Platform&apos;s operation</li>
          <li>
            Not attempt to gain unauthorized access to any part of the Platform
          </li>
        </ul>

        <h3>5.2 Prohibited Activities</h3>
        <p>You agree not to engage in any of the following prohibited activities:</p>
        <ul>
          <li>Posting false, misleading, or fraudulent information</li>
          <li>Harassing, threatening, or intimidating other users</li>
          <li>Spamming or sending unsolicited communications</li>
          <li>Violating any intellectual property rights</li>
          <li>Attempting to circumvent any security measures</li>
          <li>
            Using automated systems to access the Platform without permission
          </li>
          <li>Engaging in any illegal activities</li>
          <li>Posting inappropriate, offensive, or harmful content</li>
        </ul>

        <h3>5.3 Service Provider Responsibilities</h3>
        <p>If you are a service provider, you additionally agree to:</p>
        <ul>
          <li>Maintain all necessary licenses, permits, and insurance</li>
          <li>Provide services in a professional and competent manner</li>
          <li>Comply with all applicable building codes and safety regulations</li>
          <li>Honor all commitments and agreements made through the Platform</li>
          <li>Maintain appropriate insurance coverage for your services</li>
          <li>Provide accurate estimates and timelines for projects</li>
        </ul>
      </section>

      <section id="intellectual-property">
        <h2>6. Intellectual Property</h2>

        <h3>6.1 Maskanh&apos;s Intellectual Property</h3>
        <p>
          The Maskanh Platform, including its design, functionality, and content,
          is owned by Maskanh Inc. and is protected by intellectual property
          laws. This includes but is not limited to:
        </p>
        <ul>
          <li>The Maskanh name, logo, and trademarks</li>
          <li>Platform software, code, and algorithms</li>
          <li>User interface design and layout</li>
          <li>Documentation and help materials</li>
          <li>Proprietary data and analytics</li>
        </ul>

        <h3>6.2 User-Generated Content</h3>
        <p>
          You retain ownership of any content you post on the Platform. However,
          by posting content, you grant Maskanh a non-exclusive, royalty-free,
          worldwide license to use, display, and distribute your content in
          connection with the Platform&apos;s operation.
        </p>
        <p>
          You represent and warrant that you have the right to post any content
          you submit and that such content does not violate any third-party
          rights or applicable laws.
        </p>

        <h3>6.3 Copyright Policy</h3>
        <p>
          Maskanh respects intellectual property rights and expects users to do
          the same. If you believe your copyright has been infringed, please
          contact us with the following information:
        </p>
        <ul>
          <li>Description of the copyrighted work</li>
          <li>Location of the allegedly infringing material</li>
          <li>Your contact information</li>
          <li>
            A statement of good faith belief that the use is not authorized
          </li>
        </ul>
      </section>

      <section id="payment">
        <h2>7. Payment and Billing</h2>

        <h3>7.1 Service Fees</h3>
        <p>
          Maskanh may charge fees for certain services, including but not limited
          to:
        </p>
        <ul>
          <li>Platform usage fees for service providers</li>
          <li>Transaction processing fees</li>
          <li>Premium features and services</li>
          <li>Advertising and promotional services</li>
        </ul>

        <h3>7.2 Payment Methods</h3>
        <p>We accept various payment methods, including:</p>
        <ul>
          <li>Credit and debit cards</li>
          <li>Bank transfers</li>
          <li>Digital wallets</li>
          <li>Other payment methods as may be available</li>
        </ul>

        <h3>7.3 Billing and Invoicing</h3>
        <p>
          All fees are charged in advance or as services are rendered. You will
          receive invoices for all charges. Payment is due according to the
          terms specified in your service agreement.
        </p>

        <h3>7.4 Taxes</h3>
        <p>
          You are responsible for all applicable taxes related to your use of the
          Platform and any services you receive. Maskanh may collect and remit
          taxes as required by applicable law.
        </p>

        <h3>7.5 Refunds and Cancellations</h3>
        <p>
          Refund and cancellation policies vary depending on the specific service
          and circumstances. Generally, refunds are not provided for services
          that have been completed. Cancellation policies will be clearly stated
          at the time of booking.
        </p>
      </section>

      <section id="disclaimers">
        <h2>8. Disclaimers</h2>

        <h3>8.1 Platform Availability</h3>
        <p>
          Maskanh provides the Platform &quot;as is&quot; and &quot;as
          available.&quot; We do not guarantee that the Platform will be
          uninterrupted, error-free, or free from viruses or other harmful
          components.
        </p>

        <h3>8.2 Service Quality</h3>
        <p>
          Maskanh does not guarantee the quality, safety, or completion of any
          construction work performed by service providers. Users are responsible
          for conducting their own due diligence when selecting service providers.
        </p>

        <h3>8.3 Third-Party Services</h3>
        <p>
          The Platform may contain links to third-party websites or services.
          Maskanh is not responsible for the content, privacy policies, or
          practices of these third-party services.
        </p>

        <h3>8.4 Professional Advice</h3>
        <p>
          Information provided on the Platform is for general informational
          purposes only and does not constitute professional advice. Users
          should consult with qualified professionals for specific construction
          or legal advice.
        </p>
      </section>

      <section id="liability">
        <h2>9. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, Maskanh shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages,
          including but not limited to:
        </p>
        <ul>
          <li>Loss of profits, revenue, or business opportunities</li>
          <li>Loss of data or information</li>
          <li>Property damage or personal injury</li>
          <li>Costs of substitute services</li>
          <li>Emotional distress or mental anguish</li>
        </ul>
        <p>
          In no event shall Maskanh&apos;s total liability exceed the amount paid
          by you to Maskanh in the twelve (12) months preceding the claim.
        </p>
        <p>
          <strong>Important:</strong> This limitation of liability does not apply
          to damages caused by Maskanh&apos;s gross negligence or willful
          misconduct.
        </p>
      </section>

      <section id="indemnification">
        <h2>10. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless Maskanh and its
          officers, directors, employees, and agents from and against any
          claims, damages, losses, costs, and expenses (including reasonable
          attorneys&apos; fees) arising out of or relating to:
        </p>
        <ul>
          <li>Your use of the Platform</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any third-party rights</li>
          <li>Any content you post on the Platform</li>
          <li>Your violation of any applicable laws or regulations</li>
        </ul>
        <p>
          This indemnification obligation will survive the termination of these
          Terms and your use of the Platform.
        </p>
      </section>

      <section id="governing-law">
        <h2>11. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of Pakistan, without regard to its conflict of law principles. Any
          legal action or proceeding arising under these Terms shall be brought
          exclusively in the courts of Pakistan.
        </p>
        <p>
          If you are located outside of Pakistan, you acknowledge that you are
          using the Platform at your own risk and are responsible for compliance
          with local laws.
        </p>
      </section>

      <section id="changes">
        <h2>12. Changes to Terms</h2>
        <p>
          Maskanh reserves the right to modify these Terms at any time. We will
          notify users of any material changes by:
        </p>
        <ul>
          <li>Posting the updated Terms on the Platform</li>
          <li>Sending email notifications to registered users</li>
          <li>Displaying prominent notices on the Platform</li>
        </ul>
        <p>
          Your continued use of the Platform after any changes constitutes
          acceptance of the modified Terms. If you do not agree to the changes,
          you must stop using the Platform.
        </p>
      </section>

      <section id="termination">
        <h2>13. Termination</h2>

        <h3>13.1 Termination by Maskanh</h3>
        <p>
          Maskanh may suspend or terminate your account and access to the Platform
          at any time, with or without notice, for any reason, including but not
          limited to:
        </p>
        <ul>
          <li>Violation of these Terms</li>
          <li>Fraudulent or illegal activity</li>
          <li>Non-payment of fees</li>
          <li>Abuse of the Platform or other users</li>
          <li>Technical or security reasons</li>
        </ul>

        <h3>13.2 Termination by User</h3>
        <p>
          You may terminate your account at any time by contacting Maskanh
          support or using the account deletion feature in your account settings.
        </p>

        <h3>13.3 Effect of Termination</h3>
        <p>
          Upon termination, your right to use the Platform will cease immediately.
          Maskanh may delete your account information and content, subject to
          applicable legal requirements.
        </p>
      </section>

      <section id="contact">
        <h2>14. Contact Information</h2>
        <p>
          If you have any questions about these Terms and Conditions or need to
          contact Maskanh for any reason, please use the following information:
        </p>
        <p>
          <strong>Maskanh Inc.</strong>
        </p>
        <ul className="legal-contact-list">
          <li>
            Email:{" "}
            <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
          </li>
          <li>
            Phone:{" "}
            <a href={CONTACT.whatsappUrl}>{CONTACT.phone}</a>
          </li>
          <li>
            Website:{" "}
            <a href={CONTACT.website} target="_blank" rel="noopener noreferrer">
              www.maskanh.com
            </a>
          </li>
          <li>
            Support:{" "}
            <a href="mailto:maskanh2023@gmail.com">maskanh2023@gmail.com</a>
          </li>
        </ul>
        <p>
          Questions about these terms? Email{" "}
          <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> or message us
          on <a href={CONTACT.whatsappUrl}>WhatsApp</a>.
        </p>
      </section>
    </PageLayout>
  );
}
